import type { QueryClient } from '@tanstack/react-query';

import type { TDocument, TUseDocumentResult } from '../../documents/types/documents.types';
import type { TCustomFieldDefaults, TDraft, TVersion } from '../../documents/types/version.types';
import type { TFile, TFileVersion, TUseFileResult } from '../../files/types';
import type { TUseReplicaResult } from '../../replicas/types';
import type { TCustomFieldRaw } from '../../users/types/organizations.types';
import type { TCustomFieldValue, TItem, TItemRenameResult, TItemType, TVersionInfo } from '../types/items.types';
import type { IFolder } from '../types/folders.types';
import { docOrFileKey } from '../helpers/itemKeys.helper';
import { sortItems } from '../helpers/sortItems.helper';

type TUpsertVariables = { id: string; itemType: TItemType; fieldId: string; value: TCustomFieldRaw };
type TNameVariables = { id: string; itemType: TItemType; parentId?: string };

// Item detail query key. Local (not the @shared/helpers getQueryKey) to avoid pulling the barrel's
// runtime graph into this cache module - custom fields only exist on documents and files.
const itemQueryKey = (itemType: TItemType, id: string): string => `${itemType.toLowerCase()}-${id}`;

// TCustomFieldRaw is a one-key object union, so structural JSON comparison is exact here.
const customFieldRawEquals = (left: TCustomFieldRaw, right: TCustomFieldRaw): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

export const addItemsToFolderQueryData = (queryClient: QueryClient, folderId: string, items: readonly TItem[]) => {
  queryClient.setQueryData<IFolder>([`folder-${folderId}`], (oldData) =>
    oldData ? { ...oldData, children: sortItems([...items, ...oldData.children]) } : oldData
  );
};

const upsertCustomFieldValueEntry = (
  oldValues: TCustomFieldValue[],
  { fieldId, value }: { fieldId: string; value: TCustomFieldRaw },
  currentUserId: string
): TCustomFieldValue[] => {
  const existingValue = oldValues.find((cfv) => cfv.customField === fieldId)?.value;
  if (existingValue && customFieldRawEquals(existingValue, value)) return oldValues;

  return [
    ...oldValues.filter((cfv) => cfv.customField !== fieldId),
    { customField: fieldId, value, editor: currentUserId, editedDate: new Date().toISOString() },
  ];
};

// Custom fields are written to the draft, or to the latest version when there is no draft.
const updateEditableVersionQueryData = (
  queryClient: QueryClient,
  { id, itemType }: { id: string; itemType: TItemType },
  updateVersion: (version: TDraft | TVersion | TFileVersion) => TDraft | TVersion | TFileVersion
) => {
  const itemKey = docOrFileKey(itemType);

  const updateDraft = (draft: TDraft | undefined): TDraft | undefined =>
    draft ? (updateVersion(draft) as TDraft) : undefined;

  const updateLatestVersion = (versions: TVersion[] | TFileVersion[]) => {
    const latestVersion = versions[0];
    if (!latestVersion) return versions;

    const updatedVersion = updateVersion(latestVersion);
    if (updatedVersion === latestVersion) return versions;

    return versions.map((version, index) => (index === 0 ? updatedVersion : version)) as TVersion[] | TFileVersion[];
  };

  queryClient.setQueryData([itemQueryKey(itemType, id)], (oldData: TUseDocumentResult | TUseFileResult | undefined) => {
    const item: TDocument | TFile = (oldData as TUseDocumentResult)?.doc ?? (oldData as TUseFileResult)?.file;

    if (!oldData) return oldData;

    const draft = updateDraft(item?.draft);
    const versions = item?.draft ? item.versions : updateLatestVersion(item.versions);
    if (draft === item?.draft && versions === item.versions) return oldData;

    return { ...oldData, [itemKey]: { ...item, draft, versions } };
  });

  queryClient.setQueryData([`version-info-${id}`], (oldData: TVersionInfo | undefined): TVersionInfo | undefined => {
    if (!oldData) return oldData;

    const draft = updateDraft(oldData.draft);
    const versions = oldData.draft
      ? oldData.versions
      : updateLatestVersion(oldData.versions as TVersion[] | TFileVersion[]);
    if (draft === oldData.draft && versions === oldData.versions) return oldData;

    return { ...oldData, draft, versions };
  });
};

export const updateCustomFieldValueQueryData = (
  queryClient: QueryClient,
  variables: TUpsertVariables,
  currentUserId: string
) =>
  updateEditableVersionQueryData(queryClient, variables, (version) => {
    const customFieldValues = upsertCustomFieldValueEntry(version.customFieldValues, variables, currentUserId);
    if (customFieldValues === version.customFieldValues) return version;

    return {
      ...version,
      customFieldValues,
      ...(variables.itemType === 'DOCUMENT' ? { isPdfUpToDate: false } : {}),
    };
  });

const updateCustomFieldDefaultsQueryData = (
  queryClient: QueryClient,
  documentId: string,
  updateDefaults: (defaults: TCustomFieldDefaults) => TCustomFieldDefaults
) =>
  updateEditableVersionQueryData(queryClient, { id: documentId, itemType: 'DOCUMENT' }, (version) => ({
    ...version,
    customFieldDefaults: updateDefaults(
      (version as TDraft | TVersion).customFieldDefaults ?? { enabled: false, values: [] }
    ),
  }));

export const updateCustomFieldDefaultsEnabledQueryData = (
  queryClient: QueryClient,
  documentId: string,
  enabled: boolean
) => updateCustomFieldDefaultsQueryData(queryClient, documentId, (defaults) => ({ ...defaults, enabled }));

export const updateCustomFieldDefaultQueryData = (
  queryClient: QueryClient,
  variables: { id: string; fieldId: string; value: TCustomFieldRaw },
  currentUserId: string
) =>
  updateCustomFieldDefaultsQueryData(queryClient, variables.id, (defaults) => ({
    ...defaults,
    values: upsertCustomFieldValueEntry(defaults.values, variables, currentUserId),
  }));

// Reflect a server-side rename (e.g. unique-id filename sync) in the item cache, the
// version-info cache, and the parent folder listing.
export const updateItemNameQueryData = (
  queryClient: QueryClient,
  variables: TNameVariables,
  update: TItemRenameResult
) => {
  const itemKey = docOrFileKey(variables.itemType);

  queryClient.setQueryData(
    [itemQueryKey(variables.itemType, variables.id)],
    (oldData: TUseDocumentResult | TUseFileResult | undefined) => {
      const item: TDocument | TFile = (oldData as TUseDocumentResult)?.doc ?? (oldData as TUseFileResult)?.file;
      if (!oldData || !item) return oldData;
      return {
        ...oldData,
        [itemKey]: { ...item, name: update.name, pdfExportFileNames: update.pdfExportFileNames },
      };
    }
  );

  queryClient.setQueryData(
    [`version-info-${variables.id}`],
    (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
      oldData ? { ...oldData, name: update.name } : oldData
  );

  if (variables.parentId) queryClient.invalidateQueries({ queryKey: [`folder-${variables.parentId}`] });
};

export const updateReplicaNameQueryData = (queryClient: QueryClient, id: string, update: TItemRenameResult) => {
  queryClient.setQueryData(
    [`replica-${id}`],
    (oldData: TUseReplicaResult | undefined): TUseReplicaResult | undefined => {
      if (!oldData) return oldData;
      const masterData =
        'doc' in oldData.masterData
          ? {
              ...oldData.masterData,
              doc: { ...oldData.masterData.doc, pdfExportFileNames: update.pdfExportFileNames },
            }
          : {
              ...oldData.masterData,
              file: { ...oldData.masterData.file, pdfExportFileNames: update.pdfExportFileNames },
            };
      return {
        ...oldData,
        replica: { ...oldData.replica, name: update.name },
        masterData,
      };
    }
  );
};
